import {
  type RealTimeCommunicationRecipientDescriptor,
  type RealTimeCommunicationBootstrap,
  type RealTimeCommunicationReplayRequest,
  type RealTimeCommunicationStartOptions,
} from '@seech/core-ng';
import { NotificationHubContract } from '@automatick/shared-ts';
import { nullIfEmpty } from '@automatick/shared-ng';
import type { CurrentDeviceContextSnapshot } from '@automatick/shared-ts';
import { environment } from '../../../environments/environment';
import { appendCurrentDeviceRealtimeQuery } from './current-device-realtime-url';

export interface RealtimeStartOptionsInput {
  readonly bootstrap?: RealTimeCommunicationBootstrap | null;
  readonly hubRoute?: string | null;
  readonly receiveMethod?: string | null;
  readonly currentDeviceContext?: CurrentDeviceContextSnapshot | null;
  readonly accessTokenFactory: () => string | Promise<string>;
  readonly initialRecipients?: readonly RealTimeCommunicationRecipientDescriptor[];
  readonly replayRequests?: readonly RealTimeCommunicationReplayRequest[];
  readonly restartOnClose?: boolean;
}

export function buildRealtimeStartOptions(
  input: RealtimeStartOptionsInput,
): RealTimeCommunicationStartOptions {
  const bootstrap = input.bootstrap
    ? {
        ...input.bootstrap,
        hubRoute: buildRealtimeHubUrl(
          input.bootstrap.hubRoute,
          input.currentDeviceContext,
        ),
      }
    : null;

  return {
    bootstrap,
    hubRoute: bootstrap
      ? undefined
      : buildRealtimeHubUrl(input.hubRoute, input.currentDeviceContext),
    baseApiUrl: null,
    receiveMethod: bootstrap
      ? undefined
      : (nullIfEmpty(input.receiveMethod) ??
        NotificationHubContract.ReceiveMethod),
    subscribeMethod: bootstrap
      ? undefined
      : NotificationHubContract.SubscribeMethod,
    unsubscribeMethod: bootstrap
      ? undefined
      : NotificationHubContract.UnsubscribeMethod,
    accessTokenFactory: input.accessTokenFactory,
    initialRecipients: input.initialRecipients,
    replayRequests: input.replayRequests,
    restartOnClose: input.restartOnClose,
  };
}

export function buildRealtimeHubUrl(
  hubRoute?: string | null,
  currentDeviceContext?: CurrentDeviceContextSnapshot | null,
): string {
  const effectiveHubRoute =
    nullIfEmpty(hubRoute) ??
    environment.REALTIME_HUB_URL?.trim() ??
    NotificationHubContract.HubRoute;
  const apiBaseUrl = environment.BASE_API_URL?.trim();
  const url = !apiBaseUrl
    ? effectiveHubRoute
    : `${apiBaseUrl.replace(/\/+$/, '')}${effectiveHubRoute}`;

  return appendCurrentDeviceRealtimeQuery(
    url,
    currentDeviceContext ?? undefined,
  );
}
